import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Search, Lock, CheckCircle2, Calendar, X, FileCheck, FileX, QrCode, Sparkles, Copy, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const FAB_STAGES = ['cuttingAndFitting', 'welding', 'surfaceTreatment', 'dispatch'];
const FAB_LABELS = { cuttingAndFitting: 'Cutting & Fitting', welding: 'Welding', surfaceTreatment: 'Surface Treatment', dispatch: 'Dispatch' };
const STAGE_COLORS = { cuttingAndFitting: '#3b82f6', welding: '#f59e0b', surfaceTreatment: '#143A6B', dispatch: '#22c55e' };

function fmtWt(v) { return v != null ? `${Number(v).toFixed(1)}` : '-'; }

export default function SWMFabProgress({ projectId, filters }) {
  const [marks, setMarks] = useState([]);
  const [gateStatus, setGateStatus] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [qrMark, setQrMark] = useState(null);        // markNumber for QR modal
  const [qrMeta, setQrMeta] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const seedFabProgress = async () => {
    if (!window.confirm('Seed fabrication stage-gate progress for every 100%-allocated mark? Existing progress is preserved unless you tick force.')) return;
    setSeeding(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/seed/fab-progress`);
      toast.success(`Seeded ${data.marksSeeded}/${data.marksInScope} marks · ${data.distribution?.DISPATCH || 0} dispatched`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally { setSeeding(false); }
  };

  const openQr = async (markNumber) => {
    setQrMark(markNumber);
    setQrLoading(true);
    setQrMeta(null);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/swm/marks/metadata`, { params: { mark: markNumber } });
      setQrMeta(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Metadata failed');
      setQrMark(null);
    } finally { setQrLoading(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [marksRes, gateRes, sumRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/swm/mark-numbers`),
        api.get(`/v1/projects/${projectId}/swm/gate-status`),
        api.get(`/v1/projects/${projectId}/swm/summary`),
      ]);
      setMarks(marksRes.data?.markNumbers || []);
      setGateStatus(gateRes.data || {});
      setSummary(sumRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageClick = (markNumber, stage) => {
    const gs = gateStatus[markNumber];
    if (!gs) return;
    if (gs.fabricationBlocked) return;
    const fabStages = gs.fabStages || {};
    const idx = FAB_STAGES.indexOf(stage);
    // Allow editing completed stages (to update date) but still enforce sequential gate
    if (!fabStages[stage]?.isComplete && idx > 0 && !fabStages[FAB_STAGES[idx - 1]]?.isComplete) return;
    setEditingCell({ markNumber, stage });
    setEditDate(fabStages[stage]?.date ? String(fabStages[stage].date).slice(0, 10) : new Date().toISOString().slice(0, 10));
  };

  const handleSaveStage = async () => {
    if (!editingCell || !editDate) return;
    setSaving(true);
    try {
      await api.put(`/v1/projects/${projectId}/swm/mark-numbers/by-name/fab-stage/${editingCell.stage}?name=${encodeURIComponent(editingCell.markNumber)}`, { date: editDate });
      toast.success(`${FAB_LABELS[editingCell.stage]} recorded for ${editingCell.markNumber}`);
      setEditingCell(null);
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message || detail.error : detail;
      toast.error(msg || 'Gate enforcement blocked this update');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const fabWeights = summary?.fabricationProgress || {};
  const totalMarkWt = summary?.weights?.totalMarkWeight || 0;

  const filteredMarks = marks.filter(m => {
    if (search && !m.markNumber?.toLowerCase().includes(search.toLowerCase()) && !m.structuralTag?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filters?.area && m.area !== filters.area) return false;
    return true;
  });

  const renderStageCell = (mark, stageKey) => {
    const gs = gateStatus[mark.markNumber] || {};
    const allocPct = gs.overallAllocatedPercent || 0;
    const fabBlocked = gs.fabricationBlocked;
    const fabStages = gs.fabStages || {};
    const stageInfo = fabStages[stageKey] || {};
    const isEditing = editingCell?.markNumber === mark.markNumber && editingCell?.stage === stageKey;

    if (fabBlocked || allocPct < 100) {
      const reason = gs.fabricationBlockReason || `Hard Allocation ${allocPct.toFixed(0)}% (need 100%)`;
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-0.5 cursor-not-allowed opacity-50" data-testid={`fab-locked-${mark.markNumber}-${stageKey}`}>
                <Lock className="w-3 h-3" style={{ color: '#ef4444' }} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px] max-w-[200px]">
              <p className="font-semibold text-red-400">Fabrication Blocked</p>
              <p>{reason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (stageInfo.isComplete) {
      return (
        <button onClick={() => handleStageClick(mark.markNumber, stageKey)} className="flex flex-col items-center w-full cursor-pointer hover:opacity-80 transition-opacity" data-testid={`fab-done-${mark.markNumber}-${stageKey}`}>
          <CheckCircle2 className="w-3 h-3" style={{ color: STAGE_COLORS[stageKey] }} />
          <span className="text-[8px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{String(stageInfo.date || '').slice(0, 10)}</span>
        </button>
      );
    }

    const stageIdx = FAB_STAGES.indexOf(stageKey);
    const priorOk = stageIdx === 0 || fabStages[FAB_STAGES[stageIdx - 1]]?.isComplete;
    if (!priorOk) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center cursor-not-allowed opacity-40" data-testid={`fab-seq-locked-${mark.markNumber}-${stageKey}`}>
                <Lock className="w-3 h-3" style={{ color: '#94a3b8' }} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px]">
              <p>Complete {FAB_LABELS[FAB_STAGES[stageIdx - 1]]} first</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (isEditing) {
      return (
        <div className="flex flex-col items-center gap-0.5" data-testid={`fab-edit-${mark.markNumber}-${stageKey}`}>
          <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className="text-[9px] px-1 py-0.5 rounded border w-[90px]" style={{ background: 'var(--input-bg)', borderColor: STAGE_COLORS[stageKey], color: 'var(--text-primary)' }} />
          <div className="flex gap-0.5">
            <button onClick={handleSaveStage} disabled={saving} className="px-1 py-0.5 rounded text-[8px]" style={{ background: '#22c55e', color: '#fff' }}>
              {saving ? '...' : 'Save'}
            </button>
            <button onClick={() => setEditingCell(null)} className="px-1 py-0.5 rounded text-[8px]" style={{ background: '#ef444440', color: '#ef4444' }}>
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <button onClick={() => handleStageClick(mark.markNumber, stageKey)} className="flex items-center justify-center w-full h-full px-1 py-1 rounded hover:brightness-110 transition-all"
        style={{ background: `${STAGE_COLORS[stageKey]}10` }} data-testid={`fab-ready-${mark.markNumber}-${stageKey}`}>
        <Calendar className="w-3 h-3" style={{ color: STAGE_COLORS[stageKey] }} />
      </button>
    );
  };

  return (
    <div className="space-y-3" data-testid="swm-fab-progress">
      {/* Stage Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {FAB_STAGES.map(s => {
          const wt = fabWeights[s] || 0;
          const pct = totalMarkWt > 0 ? (wt / totalMarkWt * 100) : 0;
          return (
            <div key={s} className="p-2.5 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }} data-testid={`fab-summary-${s}`}>
              <p className="text-[9px] font-medium uppercase tracking-wide" style={{ color: STAGE_COLORS[s] }}>{FAB_LABELS[s]}</p>
              <p className="text-lg font-bold mt-0.5" style={{ color: 'var(--text-primary)' }}>{fmtWt(wt)} <span className="text-[10px] font-normal" style={{ color: 'var(--text-muted)' }}>kg</span></p>
              <div className="flex items-center gap-1 mt-1">
                <div className="flex-1 h-1.5 rounded-full" style={{ background: `${STAGE_COLORS[s]}15` }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: STAGE_COLORS[s] }} />
                </div>
                <span className="text-[9px] font-mono" style={{ color: STAGE_COLORS[s] }}>{pct.toFixed(0)}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gate Legend */}
      <div className="flex items-center gap-3 text-[10px] px-2 py-1.5 rounded-md" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
        <span style={{ color: 'var(--text-muted)' }}>Legend:</span>
        <span className="flex items-center gap-1"><Lock className="w-3 h-3" style={{ color: '#ef4444' }} /> Alloc &lt; 100%</span>
        <span className="flex items-center gap-1"><Lock className="w-3 h-3" style={{ color: '#94a3b8' }} /> Prior Stage Required</span>
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" style={{ color: '#3b82f6' }} /> Ready for Date</span>
        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" style={{ color: '#22c55e' }} /> Complete</span>
        <span className="flex items-center gap-1"><FileCheck className="w-3 h-3" style={{ color: '#22c55e' }} /> Issued</span>
        <span className="flex items-center gap-1"><FileX className="w-3 h-3" style={{ color: '#ef4444' }} /> Not Issued</span>
      </div>

      {/* Search */}
      <div className="flex items-center gap-1 px-2 py-1 rounded-md border max-w-xs" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)' }}>
        <Search className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search mark # or tag..." className="bg-transparent border-none outline-none text-[11px] w-full" style={{ color: 'var(--text-primary)' }} data-testid="swm-fab-search" />
      </div>

      <div className="flex items-center gap-2 -mt-1 mb-1">
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
          QR ▢ appears on dispatch-complete rows — click to issue barcode + BoQ metadata.
        </span>
      </div>

      {/* Mark Table */}
      <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-[10px]">
            <thead className="sticky top-0 z-10">
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                {['Mark #', 'Tag', 'Section', 'Pcs', 'Wt (kg)', 'Alloc %', 'Issued', ...FAB_STAGES.map(s => FAB_LABELS[s]), 'Progress'].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredMarks.map(mark => {
                const gs = gateStatus[mark.markNumber] || {};
                const allocPct = gs.overallAllocatedPercent || 0;
                const fabPct = gs.fabricationProgressPct || 0;
                return (
                  <tr key={mark.markNumber} className="hover:brightness-95 transition-colors" style={{ borderBottom: '1px solid var(--border-color)' }} data-testid={`fab-mark-row-${mark.markNumber}`}>
                    <td className="px-2 py-1.5 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>{mark.markNumber}</td>
                    <td className="px-2 py-1.5 font-mono text-[9px]" style={{ color: '#F59206' }}>{mark.structuralTag}</td>
                    <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{mark.section}</td>
                    <td className="px-2 py-1.5 text-center" style={{ color: 'var(--text-primary)' }}>{mark.pieceCount}</td>
                    <td className="px-2 py-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{fmtWt(mark.totalWeight)}</td>
                    <td className="px-2 py-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: allocPct >= 100 ? '#22c55e20' : allocPct > 0 ? '#f59e0b20' : '#ef444420', color: allocPct >= 100 ? '#22c55e' : allocPct > 0 ? '#f59e0b' : '#ef4444' }}>
                        {allocPct.toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-center" data-testid={`fab-issued-${mark.markNumber}`}>
                      {gs.materialIssueRequestNo ? (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center gap-0.5">
                                <FileCheck className="w-3 h-3" style={{ color: '#22c55e' }} />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-[10px]">
                              <p className="font-semibold" style={{ color: '#22c55e' }}>Material Issued</p>
                              <p className="font-mono text-[9px]">{gs.materialIssueRequestNo}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center opacity-40">
                                <FileX className="w-3 h-3" style={{ color: '#ef4444' }} />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-[10px]">
                              <p>Not Issued</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </td>
                    {FAB_STAGES.map(s => (
                      <td key={s} className="px-1 py-1 text-center" style={{ minWidth: '65px' }}>{renderStageCell(mark, s)}</td>
                    ))}
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <div className="w-10 h-1.5 rounded-full" style={{ background: '#143A6B15' }}>
                          <div className="h-full rounded-full" style={{ width: `${fabPct}%`, background: '#143A6B' }} />
                        </div>
                        <span className="text-[9px] font-mono" style={{ color: '#143A6B' }}>{fabPct.toFixed(0)}%</span>
                        {(gs.fabStages?.dispatch?.isComplete || fabPct >= 100) && (
                          <button
                            onClick={() => openQr(mark.markNumber)}
                            className="ml-1 p-0.5 rounded hover:bg-amber-500/10"
                            title="Issue QR / Barcode for this dispatched mark"
                            data-testid={`swm-mark-qr-${mark.markNumber}`}
                          >
                            <QrCode className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredMarks.length === 0 && <tr><td colSpan={12} className="text-center py-8" style={{ color: 'var(--text-muted)' }}>No marks found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {qrMark && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}
             data-testid="swm-mark-qr-modal">
          <div className="w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden"
               style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center gap-2">
                <QrCode className="w-4 h-4" style={{ color: '#f59e0b' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Mark {qrMark} · QR / Barcode
                </h3>
                {qrMeta?.serialNumber && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                    SN {qrMeta.serialNumber}
                  </span>
                )}
              </div>
              <button onClick={() => { setQrMark(null); setQrMeta(null); }} data-testid="swm-mark-qr-close">
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            {qrMeta && (
              <div className="px-5 pt-3 -mb-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const url = `/project-controls/material-tracking?prefillMark=${encodeURIComponent(qrMark)}&scan=${encodeURIComponent(JSON.stringify({v:1,p:projectId,m:qrMark,sn:qrMeta.serialNumber}))}`;
                    window.open(url, '_blank', 'noopener');
                  }}
                  className="text-[11px] px-2.5 py-1 rounded font-semibold inline-flex items-center gap-1"
                  style={{ background: '#F26B1F', color: '#fff' }}
                  data-testid="swm-mark-qr-goto-mir">
                  🡵 Go to Issue MIR (pre-filled)
                </button>
                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  Opens Material Tracking in a new tab with this mark + BoQ auto-loaded.
                </span>
              </div>
            )}
            <div className="p-5 grid grid-cols-2 gap-5">
              <div className="flex flex-col items-center gap-2">
                {qrLoading ? (
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--text-muted)' }} />
                ) : (
                  <img alt={`QR for ${qrMark}`}
                       src={`${api.defaults.baseURL}/v1/projects/${projectId}/swm/marks/qr?mark=${encodeURIComponent(qrMark)}&box_size=8`}
                       className="w-56 h-56 rounded border" style={{ borderColor: 'var(--border-color)', background: '#fff' }}
                       data-testid="swm-mark-qr-image" />
                )}
                <a href={`${api.defaults.baseURL}/v1/projects/${projectId}/swm/marks/qr?mark=${encodeURIComponent(qrMark)}&box_size=12`}
                   target="_blank" rel="noreferrer"
                   className="text-[10px] inline-flex items-center gap-1 underline"
                   style={{ color: '#f59e0b' }}>
                  <Download className="w-3 h-3" /> High-res PNG
                </a>
                <p className="text-[9px] text-center" style={{ color: 'var(--text-muted)' }}>
                  Scan in Material Tracking → Issue MIR to auto-fill mark, weight, BoQ.
                </p>
              </div>
              <div className="space-y-1 text-[11px]" data-testid="swm-mark-qr-meta">
                {qrMeta && [
                  ['Mark', qrMeta.markNumber],
                  ['Serial', qrMeta.serialNumber],
                  ['Tag', qrMeta.structuralTag],
                  ['Section', qrMeta.section],
                  ['Pieces', qrMeta.pieces],
                  ['Weight (kg)', qrMeta.weightKg],
                  ['Grade', qrMeta.grade],
                  ['Drawing', qrMeta.drawing],
                  ['Fabricator', qrMeta.fabricator],
                  ['Dispatch Date', qrMeta.dispatchDate],
                  ['Dispatch Rpt', qrMeta.dispatchReport],
                  ['BoQ Line', qrMeta.boq?.lineNumber],
                  ['Material Code', qrMeta.boq?.materialCode],
                  ['BoQ Qty', qrMeta.boq?.quantity],
                  ['UoM', qrMeta.boq?.uom],
                ].filter(([, v]) => v != null && v !== '').map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 py-0.5 border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{String(v)}</span>
                  </div>
                ))}
                {qrMeta && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(JSON.stringify(qrMeta, null, 2)); toast.success('Metadata copied'); }}
                    className="mt-2 text-[10px] inline-flex items-center gap-1"
                    style={{ color: '#f59e0b' }}
                    data-testid="swm-mark-qr-copy">
                    <Copy className="w-3 h-3" /> Copy full JSON
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
